import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { TransitionGroup } from 'react-transition-group';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
// swr
import { mutate as swrMutate } from "swr";
// @mui
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  IconButton,
  Skeleton,
  InputAdornment,
  Collapse,
  Tooltip,
  Popover,
  MenuItem,
} from '@mui/material';
import { LoadingButton } from "@mui/lab";
import { DateTimePicker, DatePicker } from '@mui/x-date-pickers-pro';
// hooks
import useResponsive from '../../hooks/useResponsive';
import useGoals from '../../hooks/useGoals';
// components
import { TimeOffStepper } from './';
import SvgIconStyle from '../../components/SvgIconStyle';
import Iconify from '../../components/Iconify';
import CompanyAvatar from '../company/CompanyAvatar';
// utils
import { fKrTrackingTimeKST } from "../../utils/formatTime";
import axios from '../../utils/axios';

const MissTimeTooltipContent = () => {
  return (
    <Box sx={{ maxWidth: "100%", display: "flex", flexDirection: "column", p: 0.5 }}>
      <Typography variant="caption">
        출근 기록을 놓치셨다면, 이번 출퇴근 기록을 완료하신 후 
      </Typography>
      <Typography variant="caption">
        놓친 시간 만큼 '출퇴근 수동 등록'을 해주세요.
      </Typography>
      <Typography variant="caption">
        출퇴근 수동 등록 버튼은 출퇴근 목록 상단에 있습니다. 
      </Typography>
    </Box>
  )
}

const OffTooltipContent = () => {
  return (
    <Box sx={{ maxWidth: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="caption">
        (1) 목표 일자가 7일 이내로 남은 미완료 업무와
      </Typography>
      <Typography variant="caption">
        (2) 오늘 완료하신 업무가 표시됩니다.
      </Typography>
    </Box>
  )
}

export default function TimeOffConfirmDialog({ timeTrackingData, isOpen, closeDialog, trackingOff, onCompanyId=null, coreTime=null }) {
  const smUp = useResponsive('up', 'sm');
  const router = useRouter();
  const { c } = router.query;
  const { enqueueSnackbar } = useSnackbar();

  const companyId = c || onCompanyId;

  const [endAt, setEndAt] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [step, setStep] = useState(0); // [0, 1, 2, 3, 4, 4.5, 5] = [퇴근시간, 투두, 요청사항추가, 요청사항완료, 메모, 코어타임확인, 확인]
  const [missTimeTooltipOpen, setMissTimeTooltipOpen] = useState(false);
  const [offTooltipOpen, setOffTooltipOpen] = useState(false);
  // 완료 투두에 추가or저장할 데이터
  const [doneData, setDoneData] = useState([]);
  const [doneContent, setDoneContent] = useState("");
  const [upcomingData, setUpcomingData] = useState([]);
  // 완료 투두중에 삭제할 데이터
  const [toBeDelete, setToBeDelete] = useState([]);
  // 다음 출근 계획
  const [nextWorkDate, setNextWorkDate] = useState("");

  // 요청사항 관련 상태
  const { goals, isLoading: isGoalsLoading, mutate: mutateGoals, createGoal, completeGoal, uncompleteGoal } = useGoals(companyId);

  // 요청사항 추가 폼 상태
  const [goalContent, setGoalContent] = useState('');
  const [goalDueDate, setGoalDueDate] = useState(null);
  const [isSubmittingGoal, setIsSubmittingGoal] = useState(false);
  const [hasAddedGoal, setHasAddedGoal] = useState(false);
  const [completedGoals, setCompletedGoals] = useState([]);
  const [todayAddedGoals, setTodayAddedGoals] = useState([]); // 오늘 추가한 요청사항

  // content dialog
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [isOpenSeeAllContentDialog, setIsOpenSeeAllContentDialog] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setIsSubmitting(false);
      setEndAt(new Date());
      setCanSubmit(true);
      setStep(0);
      setToBeDelete([]);
      setNextWorkDate("");
      setGoalContent('');
      setGoalDueDate(null);
      setIsSubmittingGoal(false);
      setHasAddedGoal(false);
      setCompletedGoals([]);
      setTodayAddedGoals([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setCanSubmit((endAt ? endAt <= new Date() : false) && (new Date(timeTrackingData?.start_at) <= endAt));
  }, [endAt]);
  
  useEffect(() => {
    if (step === 2) {
      // 요청사항 추가 단계 - 항상 다음으로 진행 가능 (추가 안해도 됨)
      setCanSubmit(true);
    } else if (step === 3) {
      // 요청사항 완료 단계 - 항상 다음으로 진행 가능 (완료 안해도 됨)
      setCanSubmit(true);
    } else if (step === 4) {
      // 다음 출근 계획 단계
      setCanSubmit(nextWorkDate.length >= 3);
    } else if (step === 4.5) {
      // 코어타임 확인 단계에서는 항상 제출 가능
      setCanSubmit(true);
    } else if (step === 1) {
      setCanSubmit(true);
    }
  }, [step, nextWorkDate]);
  
  if (!timeTrackingData) {
    return null;
  }

  const pastTime = new Date(endAt).getTime() - new Date(timeTrackingData.start_at).getTime();
  const hours = Math.floor(pastTime / (1000 * 60 * 60));
  const minutes = Math.floor((pastTime % (1000 * 60 * 60)) / (1000 * 60));

  // submit value
  const finalPastTime = new Date(endAt).getTime() - new Date(timeTrackingData.start_at).getTime();
  const finalHours = Math.floor(finalPastTime / (1000 * 60 * 60));
  const finalMinutes = Math.floor((finalPastTime % (1000 * 60 * 60)) / (1000 * 60));

  const submitTime = async () => {
    setIsSubmitting(true);
    await trackingOff({
      time_tracking_item_id: timeTrackingData.id,
      end_at: endAt.toISOString(),
      modified_start_at: null,
      modified_end_at: null,
      dones: doneData || [],
      undones: upcomingData || [],
      removes: toBeDelete || [],
      next_work_schedule: nextWorkDate || '',
    })
    // AttendancePopover의 상태 갱신
    swrMutate(`/api/works/time-tracking/crew/status/`);
    setStep(5);
    setIsSubmitting(false);
  };

  const setDoneListData = (value, index) => {
    const newDoneData = [...doneData];
    newDoneData[index].content = value;
    if (!value) {
      newDoneData.splice(index, 1);
    }
    setDoneData(newDoneData);
  };

  const goPrev = () => {
    if (step === 0) {
      return ;
    }
    if (step === 4.5) {
      setStep(4); // 코어타임 확인 단계에서 뒤로 가면 메모 단계로
      return;
    }
    setStep(step - 1);
  };

  // 코어타임 중인지 확인하는 함수 (퇴근시간 기준)
  const isInCoreTime = () => {
    if (!coreTime || !Array.isArray(coreTime) || coreTime.length === 0) {
      return false;
    }

    // 퇴근시간을 한국 시간(KST)으로 변환하여 확인
    const endTime = endAt;

    // 현지 시간을 한국 시간으로 변환
    const kstEndTime = new Date(endTime.toLocaleString("en-US", {timeZone: "Asia/Seoul"}));

    const jsWeekday = kstEndTime.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
    // 코어타임 weekday로 변환: 0=월요일, 1=화요일, ..., 6=일요일
    const endWeekday = jsWeekday === 0 ? 6 : jsWeekday - 1;
    const endTimeInMinutes = kstEndTime.getHours() * 60 + kstEndTime.getMinutes(); // 한국 시간 기준 분 단위로 변환

    // 해당 요일의 코어타임이 있는지 확인
    const targetDayCoreTime = coreTime.filter(ct => ct.weekday === endWeekday);
    if (targetDayCoreTime.length === 0) {
      return false;
    }

    // 퇴근시간이 코어타임 범위 내에 있는지 확인
    const isInRange = targetDayCoreTime.some(ct => {
      // start, end 값 추출 (객체 또는 JSON 문자열 처리)
      let startValue = ct.start;
      let endValue = ct.end;

      // 객체인 경우 .value 직접 접근
      if (typeof startValue === 'object' && startValue !== null && startValue.value) {
        startValue = startValue.value;
      }
      // JSON 문자열인 경우 파싱 후 .value 접근
      else if (typeof startValue === 'string' && startValue.trim().startsWith('{')) {
        try {
          startValue = JSON.parse(startValue).value;
        } catch (e) {}
      }

      // 객체인 경우 .value 직접 접근
      if (typeof endValue === 'object' && endValue !== null && endValue.value) {
        endValue = endValue.value;
      }
      // JSON 문자열인 경우 파싱 후 .value 접근
      else if (typeof endValue === 'string' && endValue.trim().startsWith('{')) {
        try {
          endValue = JSON.parse(endValue).value;
        } catch (e) {}
      }

      const [startHour, startMin] = startValue.split(':').map(Number);
      const [endHour, endMin] = endValue.split(':').map(Number);
      const startTime = startHour * 60 + startMin;
      const endCoreTime = endHour * 60 + endMin;
      const inRange = endTimeInMinutes >= startTime && endTimeInMinutes < endCoreTime;
      return inRange;
    });
    return isInRange;
  };

  const nextStep = () => {
    if (step === 5) {
      closeDialog();
      return ;
    }
    if (step === 4.5) {
      submitTime();
      return ;
    }
    if (step === 4) {
      // 코어타임 중인지 확인
      if (isInCoreTime()) {
        setStep(4.5); // 코어타임 확인 단계로
      } else {
        submitTime(); // 바로 제출
      }
      return ;
    }
    if (step === 0) {
      getDoneTodoItems();
    }
    if (step === 1) {
      addNewDone();
    }
    setStep(step + 1);
  }

  function getDateUnixTimestamp(date) {
    if (typeof date === "string") {
      date = new Date(date);
    }
    return Math.floor(date.getTime() / 1000);
  }

  const getDoneTodoItems = async () => {
    try {
      setIsLoading(true);
      const doneAfter = getDateUnixTimestamp(timeTrackingData?.start_at);
      const doneBefore = getDateUnixTimestamp(endAt);
      const queryParams = `?done_after=${doneAfter}&done_before=${doneBefore}`;
      const companyId = c || onCompanyId;
      const response = await axios.get(`/api/works/todo/crew/company/${companyId}/done/${queryParams}`);
      setDoneData(response.data?.done || []);
      setUpcomingData(response.data?.upcoming || []);
      setToBeDelete([]);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  const scrollToContentInput = async () => {
    const contentRef = document.getElementById('contentInput');
    contentRef.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  const enterToAdd = (e) => {
    if (e.key === "Enter" && doneContent) {
      addNewDone();
      setTimeout(scrollToContentInput, 300);
    }
  };

  const addNewDone = () => {
    if (doneContent) {
      setDoneData([...doneData, {content: doneContent}]);
      setDoneContent("");
      enqueueSnackbar("완료 투두를 추가하였습니다.", { variant: "success" });
    }
  };

  const addToDelete = (todo) => {
    setToBeDelete([...toBeDelete, todo]);
  };

  const openSellAllContentDialog = (todo) => {
    setSelectedTodo(todo);
    setIsOpenSeeAllContentDialog(true);
  };

  const doneTodo = (e, todo, index) => {
    if (todo?.id) {
      setDoneData([{content: todo?.content, id: todo?.id}, ...doneData]);
    } else {
      setDoneData([{content: todo?.content}, ...doneData]);
    }
    upcomingData.splice(index, 1);
    setUpcomingData([...upcomingData]);
    enqueueSnackbar("투두를 완료 처리하였습니다.", { variant: "success" });
  };

  const unDoneTodo = (e, todo, index) => {
    if (todo?.id) {
      setUpcomingData([...upcomingData, {content: todo?.content, id: todo?.id}]);
    } else {
      setUpcomingData([...upcomingData, {content: todo?.content}]);
    }
    doneData.splice(index, 1);
    setDoneData([...doneData]);
    enqueueSnackbar("투두를 완료 취소하였습니다.", { variant: "success" });
  };

  const removeFromUpcoming = (todo, index) => {
    if (todo.id) {
      addToDelete(todo);
    }
    upcomingData.splice(index, 1);
    setUpcomingData([...upcomingData]);
    enqueueSnackbar("투두를 삭제하였습니다.", { variant: "success" });
  };

  const removeFromDone = (todo, index) => {
    if (todo.id) {
      addToDelete(todo);
    }
    doneData.splice(index, 1);
    setDoneData([...doneData]);
    enqueueSnackbar("투두를 삭제하였습니다.", { variant: "success" });
  };

  const handleAddGoal = async () => {
    if (!goalContent.trim() || !goalDueDate || isNaN(goalDueDate.getTime())) return;

    try {
      setIsSubmittingGoal(true);
      const dueDateString = format(goalDueDate, 'yyyy-MM-dd');
      const response = await createGoal({
        content: goalContent.trim(),
        due_date: dueDateString,
      });

      // 성공 후 오늘 추가한 목록에 추가
      // response 구조가 어떻든, 입력한 데이터를 기반으로 추가
      const newGoal = {
        content: goalContent.trim(),
        due_date: dueDateString,
        // response에 id가 있으면 사용, 없으면 임시 id
        id: response?.id || response?.goal?.id || Date.now(),
        is_completed: false,
      };

      setTodayAddedGoals([...todayAddedGoals, newGoal]);

      // 폼 초기화
      setGoalContent('');
      setGoalDueDate(null);
      setHasAddedGoal(true);
    } catch (error) {
      console.error('요청사항 추가에 실패하였습니다.', error);
    } finally {
      setIsSubmittingGoal(false);
    }
  };

  const handleCompleteGoal = async (goalId) => {
    try {
      // API 호출
      await completeGoal(goalId);
      // 완료하기 전에 해당 goal을 찾아서 completedGoals에 추가
      const goalToComplete = goals.find(g => g.id === goalId);
      if (goalToComplete) {
        setCompletedGoals([...completedGoals, goalToComplete]);
      }
      // mutate는 useGoals에서 자동으로 호출됨
    } catch (error) {
      console.error('요청사항 완료 처리에 실패했습니다.', error);
    }
  };

  const handleUncompleteGoal = async (goalId) => {
    try {
      // API 호출
      await uncompleteGoal(goalId);
      // completedGoals에서 제거
      const updatedCompletedGoals = completedGoals.filter(g => g.id !== goalId);
      setCompletedGoals(updatedCompletedGoals);
      // mutate는 useGoals에서 자동으로 호출됨
    } catch (error) {
      console.error('요청사항 완료 취소에 실패했습니다.', error);
    }
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="sm" fullScreen={!smUp}>
      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: "0!important" }}>
        {/* header */}
        {step < 5 && (
        <Stack direction="row" sx={{ justifyContent: "space-between", mb: 2 }} alignItems="center">
          <IconButton color="inherit" edge="start" onClick={goPrev} sx={{ ...(step === 0 && { visibility: "hidden" })}}>
            <Iconify icon="ic:chevron-left" width={32} height={32} />
          </IconButton>
          <TimeOffStepper steps={["퇴근시간", "투두", "요청사항", "다음출근"]} step={(step === 4.5 || step === 4) ? 3 : (step === 3 ? 2 : step)} />
          {/* padding */}
          <IconButton color="inherit" edge="end" onClick={closeDialog} sx={{ visibility: "hidden" }}>
            <Iconify icon="ic:close" width={24} height={24} />
          </IconButton>
        </Stack>
        )}

        {/* step 0 */}
        {step === 0 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Typography variant={smUp ? "h4" : "h5"} gutterBottom>
            오늘 출퇴근 시간을 확인해주세요.
          </Typography>
          <Tooltip
            title={<MissTimeTooltipContent />}
            placement="bottom"
            arrow
            disableFocusListener
            disableHoverListener
            open={missTimeTooltipOpen}
            onOpen={() => setMissTimeTooltipOpen(true)}
            onClose={() => setMissTimeTooltipOpen(false)}
          >
            <Button
              startIcon={<Iconify icon="eva:info-fill" />}
              sx={{ color: "text.secondary" }}
              onClick={() => setMissTimeTooltipOpen((prev) => !prev)}
              variant="text"
            >
              출근 기록을 놓치셨나요?
            </Button>
          </Tooltip>

          <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 3 }} spacing={smUp ? 2 : 1.5}>
            <Stack spacing={1} sx={{ flexGrow: 1 }}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                출근 시간
              </Typography>
              <DateTimePicker
                readOnly
                disableMaskedInput
                views={['year', 'month', 'day', 'hours', 'minutes']}
                value={new Date(timeTrackingData?.start_at)}
                onChange={() => {}}
                renderInput={(params) => (
                  <TextField {...params} fullWidth helperText="출근 시간은 수정하실 수 없습니다." />
                )}
              />
            </Stack>
            {smUp && (
            <Stack spacing={1}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", visibility: "hidden" }}>
                -
              </Typography>
              <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary", pt: 1.5 }}>
                -
              </Typography>
            </Stack>
            )}
            <Stack spacing={1} sx={{ flexGrow: 1 }}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                퇴근 시간
              </Typography>
              <DateTimePicker
                disableMaskedInput
                views={['year', 'month', 'day', 'hours', 'minutes']}
                value={endAt}
                minDateTime={new Date(timeTrackingData?.start_at)}
                maxDateTime={new Date()}
                onChange={(newValue) => setEndAt(newValue)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth helperText="현재 시간 이전의 시간만 입력할 수 있습니다." />
                )}
              />
            </Stack>
          </Stack>

          <Box
            sx={{
              px: 3,
              py: { xs: 2, sm: 2.5 },
              my: 3,
              backgroundColor: "grey.200",
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" justifyContent={"space-between"}>
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                총 근무 시간
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
                {minutes > 0 && (
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  {hours > 0 ? `${hours}시간` : ""} {minutes}분
                </Typography>
                )}
                {!(minutes > 0) && (
                  <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  -
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>
        </>
        )}
        {/* step 1 */}
        {step === 1 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Stack direction="row" alignItems={"center"} spacing={.5} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant={smUp ? "h4" : "h5"}>
              퇴근전, 데일리 투두를 정리해주세요.
            </Typography>
            <Tooltip
              title={<OffTooltipContent />}
              placement="bottom"
              arrow
              disableFocusListener
              disableHoverListener
              open={offTooltipOpen}
              onOpen={() => setOffTooltipOpen(true)}
              onClose={() => setOffTooltipOpen(false)}
            >
              <IconButton
                onClick={() => setOffTooltipOpen((prev) => !prev)}
              >
                <Iconify icon="eva:info-fill" sx={{ color: "text.disabled" }} />
              </IconButton>
            </Tooltip>
          </Stack>
          {/* done todo */}
          {isLoading && (
            <Skeleton variant="rectangular" height={100}/>
          )}
          {!isLoading && doneData?.length === 0 && upcomingData?.length === 0 && (
            <Box
              sx={{
                my: 3,
                p: 3,
                border: "1px solid rgba(145, 158, 171, 0.24)",
                borderRadius: 2,
              }}
            >
              <Typography variant="subtitle1" sx={{ py: 2, color: "text.disabled", textAlign: "center" }}>
                오늘은 완료한 업무가 없어요.
              </Typography>
            </Box>
          )}
          <TransitionGroup>
          {!isLoading && upcomingData?.length > 0 && upcomingData.filter((todo) => toBeDelete.findIndex((d) => d.id === todo.id) === -1).map((todo, index) => (
            <Collapse key={`upcoming-${index}`}>
              <TextField
                fullWidth
                value={upcomingData[index].content}
                multiline
                maxRows={2}
                sx={{
                  "& .MuiInputBase-input": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  },
                  ':hover': {
                    cursor: 'pointer',
                  },
                  mb: 1.5,
                }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Checkbox
                        sx={{
                          ":hover": {
                            cursor: "pointer",
                          }
                        }}
                        checked={false}
                        onClick={(e) => doneTodo(e, todo, index)}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <MoreMenu
                        actions={
                          <>
                          <MenuItem onClick={() => removeFromUpcoming(todo, index)} sx={{ height: "46px" }}>
                            <Iconify icon="eva:trash-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                            투두 삭제하기
                          </MenuItem>
                          <MenuItem onClick={() => openSellAllContentDialog(todo)} sx={{ height: "46px" }}>
                            <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ mr: 1.5, width: 18, height: 18 }} />
                            내용 전체보기
                          </MenuItem>
                          </>
                        }
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Collapse>
          ))}
          {!isLoading && doneData?.length > 0 && doneData.filter((todo) => toBeDelete.findIndex((d) => d.id === todo.id) === -1).map((todo, index) => (
            <Collapse key={`done-${index}`}>
              <TextField
                fullWidth
                value={doneData[index].content}
                multiline
                maxRows={2}
                onChange={(e) => setDoneListData(e.target.value, index)}
                sx={{
                  "& .MuiInputBase-input": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  },
                  ':hover': {
                    cursor: 'pointer',
                  },
                  mb: 1.5,
                }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Checkbox
                        sx={{
                          ":hover": {
                            cursor: "pointer",
                          }
                        }}
                        checked={true}
                        onClick={(e) => unDoneTodo(e, todo, index)}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <MoreMenu
                        actions={
                          <>
                          <MenuItem onClick={() => removeFromDone(todo, index)} sx={{ height: "46px" }}>
                            <Iconify icon="eva:trash-2-outline" sx={{ mr: 1.5, width: 18, height: 18 }} />
                            투두 삭제하기
                          </MenuItem>
                          <MenuItem onClick={() => openSellAllContentDialog(todo)} sx={{ height: "46px" }}>
                            <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ mr: 1.5, width: 18, height: 18 }} />
                            내용 전체보기
                          </MenuItem>
                          </>
                        }
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Collapse>
          ))}
          </TransitionGroup>
          {!isLoading && (
            <Stack
              sx={{
                p: 3,
                bgcolor: "action.selected",
                borderRadius: 2,
              }}
              spacing={2}
            >
              <Stack
                direction="row" alignItems="center" spacing={1}
              >
                <SvgIconStyle src={`/icons/icon-plus-circle-mono.svg`} sx={{ width: 24, height: 24, color: "info.main" }} />
                <Typography variant="h6">
                  오늘 완료한 업무를 추가해보세요.
                </Typography>
              </Stack>
              <TextField
                id={"contentInput"}
                fullWidth
                placeholder="완료한 일을 적어주세요."
                value={doneContent}
                onChange={(e) => setDoneContent(e.target.value)}
                onKeyUp={enterToAdd}
                inputProps={{ maxLength: 500 }}
                sx={{
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  ":hover": {
                    cursor: "text",
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        variant={doneContent ? "contained" : "outlined"}
                        disabled={!doneContent}
                        onClick={addNewDone}
                      >
                        추가
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ width: 1, textAlign: "end" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {doneContent?.length === 0 ? "500자 이내" : `${doneContent?.length}/500`}
                </Typography>
              </Box>
            </Stack>
          )}
        </>
        )}
        {/* step 2 - 요청사항 추가 */}
        {step === 2 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Stack spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant={smUp ? "h4" : "h5"}>
              오늘 회사에서 새로 요청한 내용이 있으면 추가해주세요.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              추가한 내용은 회사에도 공유됩니다.
            </Typography>
          </Stack>

          <Stack spacing={2} sx={{ p: 2.5, bgcolor: "action.selected", borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ width: 20, height: 20, color: "primary.main" }} />
              <Typography variant="subtitle2">
                새 요청사항 등록
              </Typography>
            </Stack>

            <Box>
              <TextField
                fullWidth
                label="요청내용"
                value={goalContent}
                onChange={(e) => setGoalContent(e.target.value)}
                placeholder="예: 기능 개발 완료"
                inputProps={{ maxLength: 500 }}
                autoFocus
              />
              <Box sx={{ width: 1, textAlign: "end" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {goalContent?.length === 0 ? "500자 이내" : `${goalContent?.length}/500`}
                </Typography>
              </Box>
            </Box>

            <DatePicker
              label="기한"
              value={goalDueDate}
              onChange={setGoalDueDate}
              renderInput={(params) => <TextField {...params} fullWidth />}
              inputFormat="yyyy-MM-dd"
              mask={"____-__-__"}
              disablePast
            />

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <LoadingButton
                variant="contained"
                onClick={handleAddGoal}
                loading={isSubmittingGoal}
                disabled={!goalContent.trim() || !goalDueDate}
              >
                등록
              </LoadingButton>
            </Stack>
          </Stack>

          {/* 오늘 등록한 요청사항 목록 */}
          {todayAddedGoals.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5 }}>
                오늘 등록한 회사 요청사항
              </Typography>
              <Stack spacing={1}>
                {todayAddedGoals.map((goal, index) => (
                  <Box
                    key={`today-goal-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          mb: 0.5,
                        }}
                      >
                        {goal.content}
                      </Typography>
                      {goal.due_date && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            display: 'block',
                          }}
                        >
                          기한: {goal.due_date}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </>
        )}

        {/* step 3 - 요청사항 완료 */}
        {step === 3 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Stack spacing={1} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Typography variant={smUp ? "h4" : "h5"}>
              완료한 요청사항을 체크해주세요.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              완료 내역도 회사에 공유됩니다.
            </Typography>
          </Stack>

          {isGoalsLoading && (
            <Skeleton variant="rectangular" height={200}/>
          )}

          {!isGoalsLoading && (
            <>
              {/* 미완료 요청사항 목록 */}
              {goals.filter(g => !g.is_completed && !completedGoals.find(cg => cg.id === g.id)).length === 0 && completedGoals.length === 0 ? (
                <Box
                  sx={{
                    my: 1.5,
                    p: 2.5,
                    border: "1px solid rgba(145, 158, 171, 0.24)",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: "text.disabled", textAlign: "center" }}>
                    미완료된 요청사항이 없습니다.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TransitionGroup>
                    {goals.filter(g => !g.is_completed && !completedGoals.find(cg => cg.id === g.id)).map((goal) => (
                      <Collapse key={goal.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            ':hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "text.primary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                mb: 0.5,
                              }}
                            >
                              {goal.content}
                            </Typography>
                            {goal.due_date && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: 'block',
                                }}
                              >
                                기한: {goal.due_date}
                              </Typography>
                            )}
                          </Box>
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{
                              boxShadow: "none",
                              flexShrink: 0,
                            }}
                            onClick={() => handleCompleteGoal(goal.id)}
                          >
                            완료
                          </Button>
                        </Box>
                      </Collapse>
                    ))}
                  </TransitionGroup>

                  {/* 완료한 요청사항 목록 */}
                  {completedGoals.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5 }}>
                        완료한 요청사항
                      </Typography>
                      <TransitionGroup>
                        {completedGoals.map((goal) => (
                          <Collapse key={`completed-${goal.id}`}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1.5,
                                mb: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'grey.100',
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    mb: 0.5,
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {goal.content}
                                </Typography>
                                {goal.due_date && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      display: 'block',
                                    }}
                                  >
                                    기한: {goal.due_date}
                                  </Typography>
                                )}
                              </Box>
                              <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                sx={{
                                  boxShadow: "none",
                                  flexShrink: 0,
                                }}
                                onClick={() => handleUncompleteGoal(goal.id)}
                              >
                                완료 취소
                              </Button>
                            </Box>
                          </Collapse>
                        ))}
                      </TransitionGroup>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </>
        )}

        {/* step 4 - 다음 출근 계획 (이전 step 2) */}
        {step === 4 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Typography variant={smUp ? "h5" : "h6"} gutterBottom>
            다음 출근 계획을 공유해주세요.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
            입력하신 일정은 기업에 공유됩니다. 출근 계획에 변동이 생길 경우, 즉시 기업에 알려주시기 바랍니다.
          </Typography>
          <TextField
            id={"nextWorkDate"}
            value={nextWorkDate}
            fullWidth
            multiline
            rows={3}
            placeholder="예) 화요일 오후 2시"
            onChange={(e) => setNextWorkDate(e.target.value)}
          />
        </>
        )}
        {/* step 4.5 - 코어타임 확인 (이전 step 2.5) */}
        {step === 4.5 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Typography variant={smUp ? "h5" : "h6"} gutterBottom>
            ⛔ 지금은 코어타임입니다. 그래도 퇴근하시겠어요?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
            코어타임은 업무 성실도 판단 기준으로, 반복적인 코어타임 미준수는 기업이 '업무 불성실'로 이의 제기를 할 수 있는 사유가 되며, 계약 유지에도 영향을 줄 수 있습니다.
          </Typography>
        </>
        )}
        {/* step 5 - 완료 확인 (이전 step 3) */}
        {step === 5 && (
        <>
          <CompanyAvatar companyId={c || onCompanyId} nameVariant="body2" width={20} height={20} sx={{ color: "text.secondary", mb: 1 }} />
          <Typography variant={smUp ? "h4" : "h5"} gutterBottom>
            오늘 퇴근이 완료됐어요.
          </Typography>
          <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary", wordBreak: "keep-all", pb: 1 }}>
            완료한 일과 근무 시간을 회사에 전해드릴게요.
          </Typography>

          <Box
            sx={{
              p: 3,
              my: 2,
              backgroundColor: "grey.200",
              borderRadius: 2,
            }}
          >
            <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", mb: 0.5 }}>
              총 근무 시간
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 20, height: 20, color: "text.secondary" }} />
              <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                {finalHours > 0 ? `${finalHours}시간` : ""} {finalMinutes}분
              </Typography>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} sx={{ mt: 3 }} spacing={smUp ? 2 : 1.5}>
              <Stack spacing={.5}
                sx={{ flexGrow: 1 }}
              >
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  출근 시간
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  {fKrTrackingTimeKST(timeTrackingData?.start_at)}
                </Typography>
              </Stack>
              {smUp && (
              <Stack spacing={.5}>
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary", visibility: "hidden" }}>
                  -
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  ~
                </Typography>
              </Stack>
              )}
              <Stack spacing={.5}
                sx={{ flexGrow: 1 }}
              >
                <Typography variant={smUp ? "subtitle1" : "subtitle2" } sx={{ color: "text.secondary" }}>
                  퇴근 시간
                </Typography>
                <Typography variant={smUp ? "body1" : "body2" } sx={{ color: "text.secondary" }}>
                  {fKrTrackingTimeKST(endAt)}
                </Typography>
              </Stack>
            </Stack>
          </Box>

          <Box
            sx={{
              p: 3,
              my: 2,
              backgroundColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            {doneData && doneData.length === 0 && !nextWorkDate && (
              <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ textAlign: "center", color: "text.disabled" }}>
                완료한 투두와 다음 출근 계획이 없습니다.
              </Typography>  
            )}
            <Stack spacing={2}>
            {doneData?.length > 0 && (
              <Stack spacing={1}>
                <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
                  완료한 투두
                </Typography>
                {doneData.map((done, index) => (
                  <Typography sx={{ color: "text.secondary" }} key={index} variant={smUp ? "body1" : "body2"}>
                    • {done.content}
                  </Typography>
                ))}
              </Stack>
              )}
              {nextWorkDate && (
              <Stack spacing={1}>
                <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ color: "text.secondary" }}>
                  다음 출근 계획
                </Typography>
                <Typography sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }} variant={smUp ? "body1" : "body2"}>
                  {nextWorkDate}
                </Typography>
              </Stack>
              )}
            </Stack>
          </Box>
        </>
        )}
      </DialogContent>
      <DialogActions sx={{ m: { xs: 0, sm: 1 } }}>
        <Stack sx={{ width: 1 }} spacing={{ xs: 2, sm: 1 }}>
          <Stack
            justifyContent={"flex-end"}
            direction={smUp ? "row" : "column-reverse"}
            sx={{ width: 1 }}
            spacing={{ xs: 1, sm: 3 }}
          >
            {step < 5 && (
            <LoadingButton
              loading={isSubmitting}
              variant="text"
              onClick={closeDialog}
              size="large"
              sx={{ px: 6 }}
              fullWidth={!smUp}
            >
              취소
            </LoadingButton>
            )}
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              onClick={nextStep}
              size="large"
              sx={{ px: 6 }}
              fullWidth={!smUp}
              disabled={!canSubmit}
            >
              {step === 5 ? "확인했어요" : step === 4 ? "퇴근하기" : step === 4.5 ? "퇴근하기" : step === 2 ? (hasAddedGoal ? "다음" : "없습니다") : step === 3 ? (completedGoals.length > 0 ? "다음" : "없습니다") : "다음"}
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogActions>
      <SeeAllTodoContentDialog todo={selectedTodo} open={isOpenSeeAllContentDialog} closeDialog={() => setIsOpenSeeAllContentDialog(false)}/>
    </Dialog>
  )
};


function MoreMenu({ actions }) {
  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ ...open&&{bgcolor: "action.hover" }, borderRadius: 1 }}>
        <SvgIconStyle src={`/icons/icon-dots-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            overflow: 'inherit',
            width: 150,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'subtitle2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        {actions}
      </Popover>
    </>
  );
};

function SeeAllTodoContentDialog({ todo, open, closeDialog }) {
  return (
    <Dialog open={open} fullWidth maxWidth="dg">
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        <Stack sx={{ pb: 1 }} direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <SvgIconStyle src={`/icons/icon-eye-on-mono.svg`} sx={{ color: "text.disabled", width: 20, height: 20 }} />
            <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
              내용 전체보기
            </Typography>
          </Stack>
          <IconButton onClick={closeDialog} size="small" edge="end">
            <Iconify icon={'ic:close'} sx={{ color: "text.secondary", width: '24px', height: '24px' }} />
          </IconButton>
        </Stack>
        <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {todo?.content}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};